/*
 * @Author: Merlin
 * @Date: 2023-12-14 13:32:45
 * @LastEditors: Merlin
 * @LastEditTime: 2025-03-19 17:56:49
 * @Description:
 */

import useSettingsStore from '~/stores/settings/index';
import { Howl, Howler } from 'howler';

// Howler 在 unload / 页面切换后可能保留已 closed 的 AudioContext 引用，
// 内部 _autoResume 对其调用 resume()、unload 对其调用 close() 会抛出 DOMException。
// iOS/iPadOS 在切后台后 ctx 会进入 interrupted，resume() 可能 reject：
// InvalidStateError: Failed to start the audio device
const CLOSED_CTX_RE = /AudioContext has been closed|createBufferSource/i;

function recreateHowlerCtxFromVolume() {
    (Howler as any).ctx = null;
    (Howler as any).masterGain = null;
    (Howler as any).state = 'suspended';
    const vol = Howler.volume();
    Howler.volume(vol);
}

/** ctx 被关闭后，重新 load 曾注册的 Howl（Web Audio 解码缓存与旧 ctx 绑定，必须重载） */
function reloadSavedHowls(savedHowls: any[]) {
    for (const howl of savedHowls) {
        if (!howl) continue;
        try {
            if (howl._preload !== false && howl._preload !== 'none') {
                howl.load();
            }
        } catch {
            // 忽略单路重载失败
        }
    }
}

export function ensureHowlerAudioContext() {
    if (!import.meta.client || !Howler.usingWebAudio) return;
    const ctx = Howler.ctx as AudioContext | null;
    if (ctx && ctx.state !== 'closed') return;

    const savedHowls = [...((Howler as any)._howls || [])];
    if (savedHowls.length > 0) {
        try {
            Howler.unload();
        } catch {
            recreateHowlerCtxFromVolume();
        }
        const nextCtx = Howler.ctx as AudioContext | null;
        if (!nextCtx || nextCtx.state === 'closed') {
            recreateHowlerCtxFromVolume();
        }
        reloadSavedHowls(savedHowls);
    } else {
        recreateHowlerCtxFromVolume();
    }
}

/** 在 Howl.play 等入口调用，确保 ctx 可用后再播放 */
export function safeHowlPlay(howl: Howl, ...args: any[]) {
    ensureHowlerAudioContext();
    try {
        return howl.play(...args);
    } catch (err) {
        if (!CLOSED_CTX_RE.test(String(err))) throw err;
        ensureHowlerAudioContext();
        return howl.play(...args);
    }
}

/** 安全恢复 AudioContext（处理 suspended / interrupted） */
export function resumeHowlerAudioContext(): Promise<boolean> {
    if (!import.meta.client || !Howler.usingWebAudio) return Promise.resolve(false);
    ensureHowlerAudioContext();
    const ctx = Howler.ctx as AudioContext | null;
    if (!ctx || ctx.state === 'closed') return Promise.resolve(false);
    if (ctx.state === 'running') return Promise.resolve(true);
    if (ctx.state !== 'suspended' && ctx.state !== 'interrupted') {
        return Promise.resolve(false);
    }
    return ctx.resume().then(() => {
        (Howler as any).state = 'running';
        return true;
    }).catch(() => false);
}

function patchHowlerAutoResume() {
    if (!import.meta.client) return;
    const proto = Object.getPrototypeOf(Howler) as any;
    if (proto._safeResumePatched) return;
    if (typeof proto._autoResume !== 'function') return;
    // 完整替换而非包装：原版 ctx.resume() 无 .catch()，iOS interrupted 时会未处理 rejection
    proto._autoResume = function (this: typeof Howler) {
        const self: any = this || Howler;
        ensureHowlerAudioContext();
        const ctx = self.ctx as AudioContext | null;
        if (!ctx || typeof ctx.resume !== 'function' || !Howler.usingWebAudio) {
            return self;
        }
        if (self.state === 'running' && ctx.state !== 'interrupted' && self._suspendTimer) {
            clearTimeout(self._suspendTimer);
            self._suspendTimer = null;
        } else if (self.state === 'suspended' || (self.state === 'running' && ctx.state === 'interrupted')) {
            ctx.resume().then(() => {
                self.state = 'running';
                for (let i = 0; i < self._howls.length; i++) {
                    self._howls[i]._emit('resume');
                }
            }).catch(() => {
                // iOS 切后台 / Siri 等导致 interrupted 时 resume 可能失败，等待用户交互再试
            });
            if (self._suspendTimer) {
                clearTimeout(self._suspendTimer);
                self._suspendTimer = null;
            }
        } else if (self.state === 'suspending') {
            self._resumeAfterSuspend = true;
        }
        return self;
    };
    proto._safeResumePatched = true;
}

function setupHowlerResumeListeners() {
    if (!import.meta.client) return;
    const proto = Object.getPrototypeOf(Howler) as any;
    if (proto._resumeListenersPatched) return;
    proto._resumeListenersPatched = true;

    const resumeIfNeeded = () => {
        setTimeout(() => { resumeHowlerAudioContext(); }, 250);
    };

    document.addEventListener('touchstart', resumeIfNeeded, { passive: true });
    document.addEventListener('mousedown', resumeIfNeeded);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') resumeIfNeeded();
    });
}

function patchHowlerUnload() {
    if (!import.meta.client) return;
    const proto = Object.getPrototypeOf(Howler) as any;
    if (proto._safeUnloadPatched) return;
    const origUnload = proto.unload;
    if (typeof origUnload !== 'function') return;
    proto.unload = function (this: typeof Howler) {
        const self = this || Howler;
        const ctx = self.ctx as AudioContext | null;
        if (!ctx || ctx.state === 'closed') {
            (self as any).ctx = null;
        }
        try {
            origUnload.call(self);
        } catch {
            (self as any).ctx = null;
        }
        // ctx 已 closed 时原版 unload 会跳过 setupAudioContext，需手动补建
        if (self.usingWebAudio && (!(self as any).ctx || (self.ctx as AudioContext).state === 'closed')) {
            try {
                const vol = self.volume();
                self.volume(vol);
            } catch {
                // 忽略重建 AudioContext 失败
            }
        }
        return self;
    };
    proto._safeUnloadPatched = true;
}

function patchHowlPlay() {
    if (!import.meta.client) return;
    const proto = Howl.prototype as any;
    if (proto._safePlayPatched) return;
    const origPlay = proto.play;
    if (typeof origPlay !== 'function') return;
    proto.play = function (this: Howl, ...args: any[]) {
        ensureHowlerAudioContext();
        try {
            return origPlay.apply(this, args);
        } catch (err) {
            if (!CLOSED_CTX_RE.test(String(err))) throw err;
            ensureHowlerAudioContext();
            return origPlay.apply(this, args);
        }
    };
    proto._safePlayPatched = true;
}

function patchHowlRefreshBuffer() {
    if (!import.meta.client) return;
    const proto = Howl.prototype as any;
    if (proto._safeRefreshBufferPatched) return;
    const origRefresh = proto._refreshBuffer;
    if (typeof origRefresh !== 'function') return;
    proto._refreshBuffer = function (this: Howl, sound: any) {
        ensureHowlerAudioContext();
        try {
            return origRefresh.call(this, sound);
        } catch (err) {
            if (!CLOSED_CTX_RE.test(String(err))) throw err;
            ensureHowlerAudioContext();
            return origRefresh.call(this, sound);
        }
    };
    proto._safeRefreshBufferPatched = true;
}

function patchHowlerUnlockAudio() {
    if (!import.meta.client) return;
    const proto = Object.getPrototypeOf(Howler) as any;
    if (proto._safeUnlockPatched) return;
    const origUnlock = proto._unlockAudio;
    if (typeof origUnlock !== 'function') return;
    proto._unlockAudio = function (this: typeof Howler) {
        ensureHowlerAudioContext();
        try {
            return origUnlock.call(this || Howler);
        } catch (err) {
            if (!CLOSED_CTX_RE.test(String(err))) throw err;
            ensureHowlerAudioContext();
            try {
                return origUnlock.call(this || Howler);
            } catch {
                return this || Howler;
            }
        }
    };
    proto._safeUnlockPatched = true;
}

export function initHowlerAudioPatches() {
    patchHowlerAutoResume();
    patchHowlerUnload();
    patchHowlPlay();
    patchHowlRefreshBuffer();
    patchHowlerUnlockAudio();
    setupHowlerResumeListeners();
}

initHowlerAudioPatches();

/** HTML5 Audio play() 被 pause/stop 打断时会 reject AbortError，属正常竞态，应吞掉 */
export function safeHtmlAudioPlay(audio: HTMLAudioElement | null | undefined) {
    if (!audio) return;
    const playPromise = audio.play();
    if (playPromise?.catch) {
        playPromise.catch(() => {});
    }
}

// 🎈 Howl 文档地址：
// 🎁 https://github.com/goldfire/howler.js

// 👇 这是 oSetting 内含的的值，且在本案例中使用的值
// globalSounds: true, //【全局】 音乐是否启用标记
// globalVolume: 50, //【全局】游戏的背景音乐
// globalSoundEffects: 50, //【全局】游戏的事件音效
// inhouseGames: {}, // sound: { on: false, music: 0, effects: 0,

interface ExtendedHowl extends Howl {
    toPlay?: (...rest: any[]) => void;
    isBGM?: boolean;
}
interface MusicParams {
    src: string;
    isBGM?: boolean;
    loop?: boolean;
    volumeFrom?: 'music' | 'effects'; // 
    volumeOffset?: number, // 数值为 0.1 表示采用音量 * 0.1 的最终音量（处理一些大离谱的背景音乐）
    [key: string]: any;
}

// ↓ 销毁音乐对象后用此顶替，防止播放音乐时在控制台报错
const emptySound = new Proxy({}, {
    get(target, propertyKey, receiver) {
        // 此时音乐实例已经被销毁，无论 x.toPlay() x.toStop() 都无法执行了
        // 所以返回以下无意义函数供执行，消除报错 
        function fn(){
            if (0 && import.meta.dev) {
                console.log(`Calling Sound property: `, String(propertyKey));
            }
        };
        return fn;
    },
});

export class MusicControler<T> {
    oAudio: { [key in keyof T]: ExtendedHowl } = {} as { [key in keyof T]: ExtendedHowl };
    oSetting = useSettingsStore();
    destroyed = false;
    constructor(oMusicParams: T extends MusicParams ? T : never) {
        const { sound } = this.oSetting.inhouseGames;
        for (const [key, oParams] of Object.entries(oMusicParams)) {
            // 若 oParams.isBGM == true 将有语句继续判断 “若用户开户音乐” ，再播放
            const oConfig = {
                volume: oParams.isBGM ? sound.music : sound.effects,
                ...oParams,
                // 短音效走 Web Audio，避免 HTML5 play() 被 pause/stop 打断报 AbortError
                html5: oParams.html5 ?? !!oParams.isBGM,
                preload: oParams.preload ?? true,
                autoplay: false, // 不接受自动播放，一律在判断后再播放
            };
            // ↓ 播放失败时处理：浏览器拦截走 unlock；play/pause 竞态则忽略
            oConfig.onplayerror = (_id, err) => {
                const msg = String(err ?? '');
                if (/interrupted|abort/i.test(msg)) return;
                if (oParams.isBGM) {
                    this.oAudio[key]?.once('unlock', () => {
                        if (!this.destroyed) this.firstPlay();
                    });
                }
            };
            const oHowl = new Howl(oConfig) as ExtendedHowl;
            oHowl.isBGM = oParams.isBGM; // 保存起来用于后续判断
            const {volumeFrom, volumeOffset} = oParams;
            if (volumeFrom){
                oHowl.volume(sound[volumeFrom] * (volumeOffset || 1));
                oHowl['volumeFrom'] = volumeFrom;
            }
            if (volumeOffset) {
                oHowl['volumeOffset'] = volumeOffset;
            }
            // ↓ 这个方法可以大胆调用，它发觉用户已经关闭音乐时不会执行播放
            oHowl.toPlay = (...rest) => {
                if (!sound.on || this.destroyed) return;
                if (this.oAudio[key] !== oHowl) return;
                try {
                    ensureHowlerAudioContext();
                    resumeHowlerAudioContext();
                    // 非循环音效重复触发时先 stop，避免 HTML5 play() 与 pause/stop 竞态
                    if (!oParams.loop && oHowl.playing()) {
                        oHowl.stop();
                    }
                    oHowl.mute(false);
                    safeHowlPlay(oHowl, ...rest);
                } catch {
                    // unload 与 play 竞态时 Howler 可能抛出 playererror
                }
            };
            this.oAudio[key] ||= oHowl;
        }
        this.firstPlay(); // 播放音乐（若用户开启了音乐）
        this.watchSettingChange(); // 用户关闭音乐 & 修改音量后同步处理
        this.watchRouter(); // 设定离开游戏自动关闭音乐
    }
    // 👇 用户关闭音乐 & 修改音量后同步处理
    watchSettingChange() {
        const settingChecker = debounce(this.checkSetting, 100).bind(this);
        // ↓ 监听音乐配置并开关音乐，变更音量 ---------------------------------------
        watch(
            this.oSetting.inhouseGames.sound,
            settingChecker,
            { deep: true, immediate: true }
        );
    }
    // 👇 执行开关音乐 & 调整音量
    checkSetting(oSound) {
        if (this.destroyed) return;
        // console.log("oSound", oSound);
        // globalSounds, globalVolume, globalSoundEffects,
        const { on, music, effects } = oSound;
        Object.values(this.oAudio).forEach((cur: any) => {
            let fNewSound = cur.isBGM ? music : effects;
            if (cur.volumeFrom) { 
                fNewSound = oSound[cur.volumeFrom];
            }
            if (cur.volumeOffset > 0) { 
                fNewSound *= cur.volumeOffset;
            }
            cur.volume(fNewSound);
            cur.mute(!on);
            if (on && cur.isBGM && !cur.playing()) {
                cur.toPlay();
            }
        });
    }
    // 👇离开游戏时停止播放
    watchRouter() {
        const fnCleaner = useRouter().beforeEach((to, from) => {
            // 只判断 path 部分，
            // url 如果变化: ?name=a → ?name=b 不执行关停音乐
            if (from.path === to.path) return;
            this.toDestory();
            fnCleaner();
        });
    }
    // 👇关停所有音乐（方便外部调用）
    toDestory() {
        if (this.destroyed) return;
        this.destroyed = true;
        Object.keys(this.oAudio).forEach((key: any) => {
            const howl = this.oAudio[key];
            try {
                howl.stop();
                ensureHowlerAudioContext();
                howl.unload();
            } catch {
                // 忽略 unload 与 play 的竞态错误
            }
            // ↓ 顶替旧的，释放内存占用，也消除调用报错
            this.oAudio[key] = emptySound as any;
        });
    }
    // 👇 首次播放（可能因为浏览器策略导致播放失败）
    firstPlay() {
        if (this.destroyed) return;
        const { sound } = this.oSetting.inhouseGames;
        if (sound.on == false) return; // 不播放
        Object.values(this.oAudio).forEach((cur: any) => {
            if (cur.isBGM && !cur.playing()) {
                cur.toPlay();
            }
        });
    }
}

// 👇 防抖
function debounce(func, delay) {
    let timer; // 定义一个变量来保存定时器ID
    return function () {
        clearTimeout(timer); // 清除之前设置的定时器
        timer = setTimeout(() => {
            func.apply(this, arguments); // 调用传入的函数并将参数传递进去
        }, delay);
    };
}
